import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import {
  couponColDef,
  createNotesColDef,
  getEconomicsColumnDefs,
  lightGreyCell,
  maturityAmountColDef,
  maturityDateColDef,
  priceColDef,
  putDateColDef,
  yieldColDef,
} from '../deal-series.col-defs';
import { MaturityOrder } from '../deal-series.types';
import {
  formatMillisecondsToMonthYear,
  formatNotesWithOverrides,
  formatSpread,
  formatToThreeDecimals,
  formatUstBenchmark,
  isZeroCouponMaturity,
  orderAmountFormatter,
  shouldShowPutDateColumn,
} from '../deal-series.util';
import { isTermBondMaturity } from '../sinking-fund-schedule.util';
import { formatDateOrBlank } from '../../deal-detail/deal-detail.util';
import { createPriceChangeCellRenderer } from '../../deal-detail/reprice-window-grid.utils';
import { getFixedWidthConfig } from '../../deals.util';
import { TaxStatusEnum } from '../../deals.types';
import { SplitOrderRow } from './split-order-modal.types';
import { SPLIT_ORDER_COL_WIDTHS } from './split-order-modal.styles';
import { SplitOrderRemoveCellRenderer } from './split-order-cell-renderers';
import { isSplitRowRemovable } from '../split-order.util';

type ReadOnlyCellStyle = typeof lightGreyCell;
type NumericReadOnlyCellStyle = typeof lightGreyCell & { textAlign: 'right' };

export type SplitOrderReadOnlyColumnOptions = {
  taxStatus: TaxStatusEnum;
  securityType?: string;
  getIsInRepriceWindow: () => boolean;
  readOnlyCellStyle: ReadOnlyCellStyle;
  numericReadOnlyCellStyle: NumericReadOnlyCellStyle;
  onRemoveRow?: (rowId: string) => void;
};

const isSourceRow = (data?: SplitOrderRow): boolean => data?.rowType === 'source';

const getOrder = (data?: SplitOrderRow): MaturityOrder | undefined => data?.order;

/** Source row is pinned top but base col-def formatters blank pinned rows — fake unpinned. */
const withSourceOrder = (params: any, order: MaturityOrder | undefined) => ({
  ...params,
  data: order,
  node: { ...(params.node ?? {}), isRowPinned: () => false },
});

const createSplitPriceChangeCellRenderer = (
  getIsInRepriceWindow: () => boolean,
  flagField: keyof MaturityOrder,
  formatValue: (params: ICellRendererParams<MaturityOrder>) => string,
) => {
  const baseRenderer = createPriceChangeCellRenderer(getIsInRepriceWindow, flagField, formatValue);
  return (params: ICellRendererParams<SplitOrderRow>): string => {
    if (!isSourceRow(params.data)) {
      return '';
    }
    const order = getOrder(params.data);
    return baseRenderer(
      withSourceOrder(params, order) as unknown as ICellRendererParams<MaturityOrder>,
    );
  };
};

const adaptReadOnlyColDef = (
  base: ColDef,
  options: SplitOrderReadOnlyColumnOptions,
  width: number,
  overrides: Partial<ColDef<SplitOrderRow>> = {},
): ColDef<SplitOrderRow> => {
  const isNumeric = base.headerClass === 'ag-right-aligned-header' || base.colId === 'UST_BENCHMARK';
  const { readOnlyCellStyle, numericReadOnlyCellStyle } = options;
  const baseValueFormatter =
    typeof base.valueFormatter === 'function' ? base.valueFormatter : undefined;
  const widthConfig = overrides.resizable
    ? { width, minWidth: width, resizable: true }
    : getFixedWidthConfig(width);

  return {
    ...base,
    ...widthConfig,
    cellStyle: overrides.cellStyle ?? (isNumeric ? numericReadOnlyCellStyle : readOnlyCellStyle),
    valueGetter: overrides.valueGetter ?? ((params) => {
      if (!isSourceRow(params.data)) {
        return undefined;
      }
      const order = getOrder(params.data);
      if (typeof base.valueGetter === 'function') {
        return base.valueGetter(withSourceOrder(params, order));
      }
      if (base.field) {
        return order?.[base.field as keyof MaturityOrder];
      }
      return undefined;
    }),
    valueFormatter:
      overrides.valueFormatter ??
      (baseValueFormatter
        ? (params) => {
            if (!isSourceRow(params.data)) {
              return '';
            }
            return baseValueFormatter(withSourceOrder(params, getOrder(params.data)));
          }
        : undefined),
    ...overrides,
  };
};

const getReadOnlyColumnWidths = (
  taxStatus: TaxStatusEnum,
  securityType?: string,
): number[] => {
  const widths: number[] = [SPLIT_ORDER_COL_WIDTHS.maturity];
  if (shouldShowPutDateColumn(securityType)) {
    widths.push(SPLIT_ORDER_COL_WIDTHS.putDate);
  }
  widths.push(
    SPLIT_ORDER_COL_WIDTHS.parAmount,
    SPLIT_ORDER_COL_WIDTHS.coupon,
    SPLIT_ORDER_COL_WIDTHS.yield,
  );

  const economicsColumnDefs = getEconomicsColumnDefs(taxStatus);
  economicsColumnDefs.beforePrice.forEach(() => {
    widths.push(SPLIT_ORDER_COL_WIDTHS.spread);
  });
  widths.push(SPLIT_ORDER_COL_WIDTHS.price);
  if (economicsColumnDefs.afterPrice.length > 0) {
    widths.push(SPLIT_ORDER_COL_WIDTHS.ustSpread, SPLIT_ORDER_COL_WIDTHS.ustBenchmark);
  }
  widths.push(SPLIT_ORDER_COL_WIDTHS.notes);
  return widths;
};

export const getSplitOrderAddSplitOffset = (
  taxStatus: TaxStatusEnum,
  securityType?: string,
): number => getReadOnlyColumnWidths(taxStatus, securityType).reduce((sum, width) => sum + width, 0);

export const getSplitOrderGridMinWidth = (
  taxStatus: TaxStatusEnum,
  securityType?: string,
): number =>
  getSplitOrderAddSplitOffset(taxStatus, securityType) +
  SPLIT_ORDER_COL_WIDTHS.splitAmount +
  SPLIT_ORDER_COL_WIDTHS.conditions +
  SPLIT_ORDER_COL_WIDTHS.orderStatus +
  SPLIT_ORDER_COL_WIDTHS.rowActions;

export const buildSplitOrderReadOnlyColumnDefs = (
  options: SplitOrderReadOnlyColumnOptions,
): ColDef<SplitOrderRow>[] => {
  const { taxStatus, securityType, getIsInRepriceWindow } = options;
  const economicsColumnDefs = getEconomicsColumnDefs(taxStatus);
  const columnDefs: ColDef<SplitOrderRow>[] = [
    adaptReadOnlyColDef(maturityDateColDef, options, SPLIT_ORDER_COL_WIDTHS.maturity, {
      cellRenderer: (params) => {
        if (!isSourceRow(params.data) || !params.value) {
          return '';
        }
        const { month, year } = formatMillisecondsToMonthYear(params.value);
        return `<span>${month}/</span><b>${year}</b>`;
      },
      cellStyle: options.readOnlyCellStyle,
    }),
  ];

  if (shouldShowPutDateColumn(securityType)) {
    columnDefs.push(
      adaptReadOnlyColDef(putDateColDef, options, SPLIT_ORDER_COL_WIDTHS.putDate, {
        valueFormatter: (params) =>
          isSourceRow(params.data) ? formatDateOrBlank(params.value) : '',
      }),
    );
  }

  columnDefs.push(
    adaptReadOnlyColDef(maturityAmountColDef, options, SPLIT_ORDER_COL_WIDTHS.parAmount, {
      cellClassRules: {
        'term-bond-par-amount': (params) =>
          isSourceRow(params.data) &&
          (isTermBondMaturity(getOrder(params.data)?.SINKING_FUND_SCHEDULE) ||
            isZeroCouponMaturity(getOrder(params.data)?.SECURITY_TYPE)),
      },
      valueFormatter: undefined,
      cellRenderer: createSplitPriceChangeCellRenderer(
        getIsInRepriceWindow,
        'MATURITY_AMOUNT_CHANGED',
        (params) => orderAmountFormatter(params.value),
      ),
    }),
    adaptReadOnlyColDef(couponColDef, options, SPLIT_ORDER_COL_WIDTHS.coupon, {
      valueFormatter: undefined,
      cellRenderer: createSplitPriceChangeCellRenderer(
        getIsInRepriceWindow,
        'COUPON_CHANGED',
        (params) => formatToThreeDecimals(params.value),
      ),
    }),
    adaptReadOnlyColDef(yieldColDef, options, SPLIT_ORDER_COL_WIDTHS.yield, {
      valueFormatter: undefined,
      cellRenderer: createSplitPriceChangeCellRenderer(
        getIsInRepriceWindow,
        'YIELD_CHANGED',
        (params) => formatToThreeDecimals(params.value),
      ),
    }),
  );

  economicsColumnDefs.beforePrice.forEach((colDef) => {
    columnDefs.push(
      adaptReadOnlyColDef(colDef, options, SPLIT_ORDER_COL_WIDTHS.spread, {
        valueFormatter: (params) =>
          isSourceRow(params.data) ? formatSpread(params.value) : '',
      }),
    );
  });

  columnDefs.push(
    adaptReadOnlyColDef(priceColDef, options, SPLIT_ORDER_COL_WIDTHS.price, {
      valueFormatter: undefined,
      cellRenderer: createSplitPriceChangeCellRenderer(
        getIsInRepriceWindow,
        'PRICE_CHANGED',
        (params) => formatToThreeDecimals(params.value),
      ),
    }),
  );

  economicsColumnDefs.afterPrice.forEach((colDef, index) => {
    columnDefs.push(
      adaptReadOnlyColDef(
        colDef,
        options,
        index === 0 ? SPLIT_ORDER_COL_WIDTHS.ustSpread : SPLIT_ORDER_COL_WIDTHS.ustBenchmark,
        colDef.colId === 'UST_BENCHMARK'
          ? {
              valueGetter: (params) => {
                if (!isSourceRow(params.data)) {
                  return '';
                }
                const order = getOrder(params.data);
                return formatUstBenchmark(order?.TAX_TREASURY_COUPON, order?.TAX_TREASURY_MATURITY_DATE);
              },
            }
          : {
              valueFormatter: (params) =>
                isSourceRow(params.data) ? formatSpread(params.value) : '',
            },
      ),
    );
  });

  columnDefs.push(
    adaptReadOnlyColDef(createNotesColDef(), options, SPLIT_ORDER_COL_WIDTHS.notes, {
      colId: 'notes',
      resizable: true,
      cellRendererSelector: (params) => {
        const data = params.data;
        if (data?.rowType === 'split' && isSplitRowRemovable(data) && options.onRemoveRow) {
          return {
            component: SplitOrderRemoveCellRenderer,
            params: { onRemoveRow: options.onRemoveRow },
          };
        }
        return undefined;
      },
      valueGetter: (params) => {
        if (!isSourceRow(params.data)) {
          return '';
        }
        const order = getOrder(params.data);
        return order ? formatNotesWithOverrides(order) : '';
      },
    }),
  );

  return columnDefs;
};
